/**
 * ตรวจรายการหักตามเวลา — หน้าเต็มมี URL ของตัวเอง (/owner/payroll/docking-review)
 *
 * ทำไมต้องมีหน้านี้: ระบบคิดยอดหัก ลาไม่รับค่าจ้าง / ขาดงาน / มาสาย ให้อัตโนมัติได้
 * ก็จริง แต่ยอดอัตโนมัติเชื่อ 100% ไม่ได้ — วันขาดงานบางวันคือ "ลืมสแกน / GPS ไม่ผ่าน"
 * ไม่ใช่ขาดจริง และบางเคสตกลงกับพนักงานไว้แล้ว. หน้านี้ให้คนเคาะรายคนก่อนกดทำเงินเดือน
 * แล้วเอนจินจะใช้ยอดที่เคาะแทนยอดอัตโนมัติ (payroll_docking_reviews)
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Save, RefreshCw, CalendarClock, ArrowRight } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { useToast } from '../../components/ui/toast';
import { ApiError } from '../../lib/api-client';
import { useConfirm } from '../../components/ui/confirm';
import {
  useDockingReview, useSaveDockingReview,
  type DockingReviewRow, type DockingReviewSaveRow,
} from '../../hooks';

// U10 — สร้างจากวันที่ 1 ของแต่ละเดือนเสมอ · setMonth(-1) บนวันที่ 31 จะข้ามเดือน
// (31 มี.ค. → setMonth(-1) ได้ 3 มี.ค. ไม่ใช่ ก.พ.) ทำให้รายการงวดหายไปหนึ่งเดือน
function getLast6Periods(): string[] {
  const now = new Date();
  const periods: string[] = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    periods.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return periods;
}

const baht = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** สถานะที่แก้ได้ในตาราง — ทุกช่องเก็บเป็น string เพื่อให้พิมพ์/ลบทิ้งกลางคันได้ */
interface Draft {
  deduct_unpaid: boolean; unpaid_amount: string;
  deduct_absent: boolean; absent_amount: string;
  deduct_late: boolean; late_amount: string;
  note: string;
}

/** สถานะของคำเคาะแถวนี้ — แยก "ยังไม่เคยตรวจ" ออกจาก "ตรวจแล้วแต่ข้อมูลเปลี่ยน" */
export type ReviewState = 'none' | 'valid' | 'stale';

export function reviewStateOf(r: Pick<DockingReviewRow, 'saved' | 'stale'>): ReviewState {
  if (!r.saved) return 'none';
  return r.stale ? 'stale' : 'valid';
}

/**
 * ค่าเริ่มต้นของแถว
 *  · ตรวจแล้วและยังไม่ล้าสมัย → ใช้ที่บันทึกไว้
 *  · นอกนั้น → ยอดที่ระบบคิดให้ แต่ชนิดที่ "ไฟล์นำเข้ามีรายการคล้ายกัน" ให้ติ๊กออก
 *    ไว้ก่อน (กันหักซ้ำ) HR ติ๊กกลับได้ถ้าดูแล้วไม่ซ้ำจริง
 */
export function draftFrom(r: DockingReviewRow): Draft {
  if (reviewStateOf(r) === 'valid') {
    const sv = r.saved!;
    return {
      deduct_unpaid: sv.deduct_unpaid, unpaid_amount: String(sv.unpaid_amount ?? 0),
      deduct_absent: sv.deduct_absent, absent_amount: String(sv.absent_amount ?? 0),
      deduct_late: sv.deduct_late, late_amount: String(sv.late_amount ?? 0),
      note: sv.note ?? '',
    };
  }
  const fd = r.file_deducted_satang ?? {};
  const money = (n: number) => String(Math.round(n * 100) / 100);
  // ติ๊กตาม "ประตูอนุญาตไหม + มียอดให้หักไหม" — ยอดที่ส่งมาเป็นยอดก่อนชนเพดาน
  // จึงสะท้อนความจริงว่ามีอะไรให้หัก (ยอดหลัง clamp อาจเป็น 0 ทั้งที่มีวันลาจริง)
  return {
    deduct_unpaid: r.allowed.unpaid_leave && r.auto.unpaid_leave > 0 && !(Number(fd.unpaid_leave ?? 0) > 0),
    unpaid_amount: money(r.auto.unpaid_leave),
    deduct_absent: r.allowed.absent && r.auto.absent > 0 && !(Number(fd.absent ?? 0) > 0),
    absent_amount: money(r.auto.absent),
    deduct_late: r.allowed.late && r.auto.late > 0 && !(Number(fd.late ?? 0) > 0),
    late_amount: money(r.auto.late),
    note: '',
  };
}

function autoDraft(r: DockingReviewRow): Draft {
  return draftFrom({ ...r, saved: null, stale: false });
}

export function OwnerDockingReview() {
  const toast = useToast();
  const confirm = useConfirm();
  const [period, setPeriod] = useState<string>(getLast6Periods()[0] ?? '');
  const [showAll, setShowAll] = useState(false);
  // R4 — ผูก drafts กับงวด: เปลี่ยนงวดแล้วค่าที่แก้ค้างของงวดเก่าต้องไม่ตกไปใส่งวดใหม่
  const [draftState, setDraftState] = useState<{ period: string; drafts: Record<string, Draft> }>({ period: '', drafts: {} });
  const drafts = draftState.period === period ? draftState.drafts : {};
  const setDrafts = (next: Record<string, Draft> | ((p: Record<string, Draft>) => Record<string, Draft>)) =>
    setDraftState((prev) => ({
      period,
      drafts: typeof next === 'function' ? next(prev.period === period ? prev.drafts : {}) : next,
    }));

  const { data, isLoading, refetch, isFetching } = useDockingReview(period);
  const save = useSaveDockingReview();

  // โหลดงวดใหม่ → ตั้งค่าเริ่มต้นของทุกแถวใหม่ทั้งชุด
  // N2 — ซิงก์ค่าเริ่มต้นจากเซิร์ฟเวอร์ "เฉพาะตอนงวดนั้นยังไม่มี draft"
  //
  // เดิมใช้ ref flag + queueMicrotask กันไม่ให้ effect ทับค่าที่เพิ่งคำนวณใหม่ — ใช้ไม่ได้
  // เพราะ React flush passive effect เป็น task ไม่ใช่ microtask ธงจึงถูกล้างก่อน effect
  // ได้รัน แล้วค่าที่คำนวณใหม่ถูก draftFrom(saved) ทับ. เปลี่ยนมาให้ตัว state กันตัวเอง:
  // ถ้า draftState ผูกกับงวดนี้อยู่แล้ว = มีคนตั้งค่าไว้ (โหลดครั้งแรก/คำนวณใหม่/โหลดใหม่)
  // → ไม่แตะซ้ำ ไม่ต้องมีธงอะไรทั้งสิ้น
  useEffect(() => {
    if (!data?.rows) return;
    if (draftState.period === period) return;
    const next: Record<string, Draft> = {};
    for (const r of data.rows) next[r.emp_code] = draftFrom(r);
    setDraftState({ period, drafts: next });
  }, [data, period, draftState.period]);

  const readOnly = !!data?.locked;
  // U5/U7 — โหลดใหม่ไม่สำเร็จ หรือยังไม่ได้ติดตั้งตาราง → ห้ามบันทึก (จะเขียนทับ
  // ด้วยข้อมูลที่เราไม่รู้ว่าตรงกับของจริงหรือเปล่า)
  const [refetchFailed, setRefetchFailed] = useState(false);
  const tableMissing = !!data?.review_table_missing;
  const saveBlocked = readOnly || refetchFailed || tableMissing;

  const visibleRows = useMemo(() => {
    const rows = data?.rows ?? [];
    if (showAll) return rows;
    return rows.filter((r) => r.unpaid_leave_days > 0 || r.absent_days > 0 || r.late_minutes > 0);
  }, [data, showAll]);

  const num = (v: string) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const rowTotal = (r: DockingReviewRow, d: Draft | undefined) => {
    if (!d) return 0;
    return (
      (r.allowed.unpaid_leave && d.deduct_unpaid ? num(d.unpaid_amount) : 0) +
      (r.allowed.absent && d.deduct_absent ? num(d.absent_amount) : 0) +
      (r.allowed.late && d.deduct_late ? num(d.late_amount) : 0)
    );
  };

  // U1 — ยอดที่โชว์เป็นยอดก่อนชนเพดาน ต้องบอกเองว่าใครรวมแล้วเกินค่าจ้าง
  const overBudget = useMemo(() => {
    const out: Array<{ name: string; cap: number }> = [];
    for (const r of data?.rows ?? []) {
      const t = rowTotal(r, drafts[r.emp_code]);
      if (r.pay_base > 0 && t > r.pay_base) out.push({ name: r.name, cap: r.pay_base });
    }
    return out;
  }, [data, drafts]);

  const summary = useMemo(() => {
    let people = 0;
    let total = 0;
    for (const r of data?.rows ?? []) {
      const t = rowTotal(r, drafts[r.emp_code]);
      if (t > 0) { people++; total += t; }
    }
    return { people, total };
  }, [data, drafts]);

  function setField(code: string, patch: Partial<Draft>) {
    setDrafts((prev) => ({ ...prev, [code]: { ...prev[code]!, ...patch } }));
  }

  function toggleColumn(kind: 'unpaid' | 'absent' | 'late', on: boolean) {
    setDrafts((prev) => {
      const next = { ...prev };
      for (const r of visibleRows) {
        const d = next[r.emp_code];
        if (!d) continue;
        if (kind === 'unpaid' && r.allowed.unpaid_leave) next[r.emp_code] = { ...d, deduct_unpaid: on };
        if (kind === 'absent' && r.allowed.absent) next[r.emp_code] = { ...d, deduct_absent: on };
        if (kind === 'late' && r.allowed.late) next[r.emp_code] = { ...d, deduct_late: on };
      }
      return next;
    });
  }

  async function handleRecalc() {
    const ok = await confirm({
      title: 'คำนวณยอดใหม่ทับของเดิม',
      message: 'ระบบจะดึงข้อมูลเวลาล่าสุดมาคำนวณใหม่ แล้วแทนที่ยอดที่แก้ไว้เองในตาราง (หมายเหตุที่พิมพ์ไว้จะถูกล้างด้วย) — ยังไม่บันทึกจนกว่าจะกด "บันทึก"',
      confirmText: 'คำนวณใหม่',
    });
    if (!ok) return;
    // ต้องดึงใหม่จากเซิร์ฟเวอร์ ไม่ใช่คำนวณจาก cache เดิม ไม่งั้น "คำนวณใหม่" ได้ยอดเก่า
    const fresh = await refetch();
    if (fresh.isError || !fresh.data) {
      setRefetchFailed(true);
      toast.error('ดึงข้อมูลล่าสุดไม่สำเร็จ — ยังไม่คำนวณใหม่ให้ ลองกด "โหลดใหม่" อีกครั้ง');
      return;
    }
    setRefetchFailed(false);
    const next: Record<string, Draft> = {};
    for (const r of fresh.data.rows) next[r.emp_code] = autoDraft(r);
    setDraftState({ period, drafts: next });
    toast.success('ดึงข้อมูลล่าสุดแล้วคำนวณใหม่ — ตรวจแล้วกดบันทึก');
  }

  async function reloadFromServer(msg: string) {
    const fresh = await refetch();
    if (fresh.isError || !fresh.data) {
      setRefetchFailed(true);
      toast.error('ข้อมูลเวลาเปลี่ยน แต่โหลดใหม่ไม่สำเร็จ — กด "โหลดใหม่" อีกครั้งก่อนบันทึก');
      return;
    }
    setRefetchFailed(false);
    const next: Record<string, Draft> = {};
    for (const r of fresh.data.rows) next[r.emp_code] = draftFrom(r);
    setDraftState({ period, drafts: next });
    toast.error(msg);
  }

  async function handleSave() {
    const rows = data?.rows ?? [];
    const payload: DockingReviewSaveRow[] = [];
    for (const r of rows) {
      const d = drafts[r.emp_code];
      if (!d) continue;
      const hasSomething =
        r.unpaid_leave_days > 0 || r.absent_days > 0 || r.late_minutes > 0 ||
        num(d.unpaid_amount) > 0 || num(d.absent_amount) > 0 || num(d.late_amount) > 0 ||
        d.note.trim() !== '';
      if (!hasSomething) continue;

      // U2/U9 — ตรวจเฉพาะช่องที่ "ติ๊กหัก" ไว้: ติ๊กแล้วยอดว่าง/0/ไม่ใช่ตัวเลข
      // = ตั้งใจหักแต่ไม่ได้หัก (เงียบ) ต้องบล็อกตั้งแต่ยังไม่ยิง
      const checks: Array<[boolean, string, string]> = [
        [d.deduct_unpaid, d.unpaid_amount, 'หักลาไม่รับค่าจ้าง'],
        [d.deduct_absent, d.absent_amount, 'หักขาดงาน'],
        [d.deduct_late, d.late_amount, 'หักมาสาย'],
      ];
      for (const [ticked, raw, label] of checks) {
        if (!ticked) continue;
        if (!(num(raw) > 0)) {
          toast.error(`${r.name}: ติ๊ก "${label}" ไว้แต่ยอดยังไม่ถูกต้อง — ใส่ยอดมากกว่า 0 หรือติ๊กออก`);
          return;
        }
      }
      const total = rowTotal(r, d);
      if (r.pay_base > 0 && total > r.pay_base) {
        toast.error(`${r.name}: ยอดหักรวม ${baht(total)} เกินค่าจ้างของงวด ${baht(r.pay_base)}`);
        return;
      }

      payload.push({
        emp_code: r.emp_code,
        src_hash: r.src_hash,
        expected_revision: r.saved?.revision ?? null,
        deduct_unpaid: d.deduct_unpaid, unpaid_amount: num(d.unpaid_amount),
        deduct_absent: d.deduct_absent, absent_amount: num(d.absent_amount),
        deduct_late: d.deduct_late, late_amount: num(d.late_amount),
        note: d.note.trim() ? d.note.trim() : null,
      });
    }
    if (payload.length === 0) { toast.error('ไม่มีรายการให้บันทึก'); return; }
    try {
      const res = await save.mutateAsync({ period, rows: payload });
      if (res.needs_rerun) {
        toast.error(`บันทึกแล้ว ${res.saved} คน — งวดนี้รันร่างไปก่อนหน้านี้แล้ว ต้องกลับไป "รันร่างเงินเดือน" ใหม่ ตัวเลขถึงจะตรง`);
      } else {
        toast.success(`บันทึกแล้ว ${res.saved} คน — ตอนทำเงินเดือนระบบจะใช้ยอดนี้`);
      }
    } catch (e) {
      // U8 — จับจาก HTTP status ไม่ใช่เนื้อข้อความ (ข้อความเปลี่ยนเมื่อไหร่ก็พังเงียบ)
      if (e instanceof ApiError && e.status === 409) {
        await reloadFromServer('ข้อมูลเปลี่ยนหลังจากเปิดหน้านี้ โหลดใหม่ให้แล้ว — ตรวจอีกครั้งแล้วกดบันทึก');
        return;
      }
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="ตรวจรายการหักตามเวลา">
      <div className="card p-4 mb-3">
        <label className="label">เลือกงวด</label>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className="input">
          {getLast6Periods().map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <p className="text-xs text-gray-500 mt-2">
          ยอดที่เคาะไว้ที่นี่จะถูกใช้ตอนกด &ldquo;รันร่างเงินเดือน&rdquo; แทนยอดที่ระบบคิดเอง ·
          คนที่ไม่ได้บันทึกไว้ ระบบจะใช้ยอดอัตโนมัติ
        </p>
      </div>

      {isLoading ? <Loading /> : !data ? (
        <p className="py-8 text-center text-gray-500">โหลดข้อมูลไม่สำเร็จ</p>
      ) : data.needs_reconcile ? (
        <div className="card p-4">
          <p className="font-medium mb-1">ยังไม่มีข้อมูลเวลาของงวด {period}</p>
          <p className="text-sm text-gray-600 mb-3">
            ต้องกด &ldquo;สรุปเวลาเข้างาน&rdquo; ของงวดนี้ก่อน ระบบถึงจะรู้ว่าใครลาไม่รับค่าจ้าง ขาดงาน หรือมาสายกี่นาที
          </p>
          <Link to="/owner/cycle#reconcile" className="btn-primary">
            <CalendarClock className="w-4 h-4" /> ไปหน้าสรุปเวลาเข้างาน <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <>
          {/* แถบสรุป */}
          <div className="card p-4 mb-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div>
                <p className="text-xs text-gray-500">คนที่จะถูกหัก</p>
                <p className="text-lg font-semibold">{summary.people} คน</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">ยอดรวมที่จะหัก</p>
                <p className="text-lg font-semibold">{baht(summary.total)} บาท</p>
              </div>
              <div className="ml-auto">
                <span className="badge whitespace-nowrap">
                  {data.reviewed_count > 0
                    ? `ตรวจแล้ว ${data.reviewed_count} คน${data.reviewed_by ? ` โดย ${data.reviewed_by}` : ''}${data.reviewed_at ? ` เมื่อ ${new Date(data.reviewed_at).toLocaleString('th-TH')}` : ''}`
                    : 'ยังไม่ได้ตรวจ'}
                </span>
              </div>
            </div>
            {readOnly && (
              <p className="text-sm text-gray-600 mt-3">
                งวดนี้ปิดแล้ว — ดูได้อย่างเดียว แก้ไม่ได้ (ต้องยกเลิกการปิดงวดก่อน)
              </p>
            )}
            {tableMissing && (
              <p className="text-sm text-red-600 mt-3">
                ยังไม่ได้ติดตั้งตารางเก็บผลตรวจ — บันทึกไม่ได้ (ต้องรัน migration
                20260905010000_payroll_docking_reviews.sql ก่อน)
              </p>
            )}
            {refetchFailed && (
              <p className="text-sm text-red-600 mt-3">
                โหลดข้อมูลล่าสุดไม่สำเร็จ — ปิดการบันทึกไว้ก่อน กด &ldquo;โหลดใหม่&rdquo; ให้สำเร็จก่อนบันทึก
              </p>
            )}
            {(data.decrypt_failed ?? []).length > 0 && (
              <p className="text-sm text-red-600 mt-3">
                ถอดรหัสฐานเงินเดือนไม่ได้ {(data.decrypt_failed ?? []).length} คน
                ({(data.decrypt_failed ?? []).slice(0, 10).join(', ')}) — คนกลุ่มนี้ไม่อยู่ในตารางด้านล่าง
                และจะไม่ถูกคิดเงินเดือนงวดนี้ ต้องแก้ก่อน
              </p>
            )}
            {overBudget.length > 0 && (
              <p className="text-sm text-red-600 mt-3">
                ยอดหักรวมเกินค่าจ้างของงวด {overBudget.length} คน — ระบบจะหักได้สูงสุดเท่าค่าจ้างที่มี
                ({overBudget.slice(0, 5).map((o) => `${o.name} สูงสุด ${baht(o.cap)}`).join(' · ')})
              </p>
            )}
          </div>

          {/* ปุ่มจัดการ */}
          <div className="card p-3 mb-3 flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none mr-2">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="w-4 h-4 accent-brand-600" />
              แสดงทุกคน
            </label>
            <button
              onClick={async () => {
                const r = await refetch();
                // R8 — สำเร็จแล้วต้องปลดล็อกปุ่มบันทึก ไม่งั้นติดค้างถาวร
                setRefetchFailed(r.isError || !r.data);
              }}
              disabled={isFetching}
              className="btn-secondary btn-slim"
            >
              <RefreshCw className="w-3.5 h-3.5" /> {isFetching ? 'กำลังโหลด…' : 'โหลดใหม่'}
            </button>
            <button onClick={handleRecalc} disabled={readOnly || tableMissing} className="btn-secondary btn-slim">
              <RefreshCw className="w-3.5 h-3.5" /> คำนวณใหม่
            </button>
            <button onClick={() => toggleColumn('unpaid', true)} disabled={readOnly} className="btn-secondary btn-slim">ติ๊กหักลาทั้งหมด</button>
            <button onClick={() => toggleColumn('unpaid', false)} disabled={readOnly} className="btn-secondary btn-slim">เอาออกทั้งหมด</button>
            <button onClick={() => toggleColumn('absent', true)} disabled={readOnly} className="btn-secondary btn-slim">ติ๊กหักขาดทั้งหมด</button>
            <button onClick={() => toggleColumn('absent', false)} disabled={readOnly} className="btn-secondary btn-slim">เอาออกทั้งหมด</button>
            <button onClick={() => toggleColumn('late', true)} disabled={readOnly} className="btn-secondary btn-slim">ติ๊กหักสายทั้งหมด</button>
            <button onClick={() => toggleColumn('late', false)} disabled={readOnly} className="btn-secondary btn-slim">เอาออกทั้งหมด</button>
            <button onClick={handleSave} disabled={saveBlocked || save.isPending} className="btn-primary btn-slim ml-auto">
              <Save className="w-3.5 h-3.5" /> {save.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
            </button>
          </div>

          {visibleRows.length === 0 ? (
            <div className="card p-6 text-center text-gray-500">
              <p>งวดนี้ไม่มีใครลาไม่รับค่าจ้าง ขาดงาน หรือมาสายเลย</p>
              <p className="text-sm mt-1">ติ๊ก &ldquo;แสดงทุกคน&rdquo; ถ้าต้องการดูรายชื่อทั้งหมด</p>
            </div>
          ) : (
            <div className="card p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-[var(--border)]">
                    <th className="px-3 py-2 whitespace-nowrap">รหัส</th>
                    <th className="px-3 py-2 whitespace-nowrap">ชื่อ</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">ฐาน</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">ลา (วัน)</th>
                    <th className="px-3 py-2 whitespace-nowrap">หักลา</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">ขาด (วัน)</th>
                    <th className="px-3 py-2 whitespace-nowrap">หักขาด</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">สาย (นาที)</th>
                    <th className="px-3 py-2 whitespace-nowrap">หักสาย</th>
                    <th className="px-3 py-2 whitespace-nowrap">หมายเหตุ</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">รวมหัก</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">สุทธิโดยประมาณ</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => {
                    const d = drafts[r.emp_code];
                    if (!d) return null;
                    const total = rowTotal(r, d);
                    const cell = (
                      allowed: boolean, checked: boolean, amount: string,
                      onCheck: (v: boolean) => void, onAmount: (v: string) => void,
                    ) => (
                      allowed ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            type="checkbox" checked={checked} disabled={readOnly}
                            onChange={(e) => onCheck(e.target.checked)}
                            className="w-4 h-4 accent-brand-600 shrink-0"
                          />
                          <input
                            type="number" min="0" step="0.01" value={amount} disabled={readOnly || !checked}
                            onChange={(e) => onAmount(e.target.value)}
                            className="input py-1 px-2 text-right w-24"
                          />
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 whitespace-nowrap">ไม่หัก</span>
                      )
                    );
                    return (
                      <tr key={r.emp_code} className="border-b border-[var(--border)] last:border-0 align-middle">
                        <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{r.emp_code}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {r.name}
                          {r.skip_reason && (
                            <span className="badge badge-warn ml-1.5 whitespace-nowrap">{r.skip_reason}</span>
                          )}
                          {reviewStateOf(r) === 'stale' && (
                            <span className="badge badge-warn ml-1.5 whitespace-nowrap">ข้อมูลเวลาเปลี่ยน ต้องตรวจใหม่</span>
                          )}
                          {(r.file_labels ?? []).map((f, i) => (
                            <span key={`${f.label}-${i}`} className="badge badge-warn ml-1.5 whitespace-nowrap">
                              ไฟล์มีรายการ &ldquo;{f.label}&rdquo; ฿{Number(f.amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })} อาจซ้ำ
                            </span>
                          ))}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right">{baht(r.pay_base)}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-right">{r.unpaid_leave_days || '-'}</td>
                        <td className="px-3 py-2">{cell(r.allowed.unpaid_leave, d.deduct_unpaid, d.unpaid_amount,
                          (v) => setField(r.emp_code, { deduct_unpaid: v }), (v) => setField(r.emp_code, { unpaid_amount: v }))}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-right">{r.absent_days || '-'}</td>
                        <td className="px-3 py-2">{cell(r.allowed.absent, d.deduct_absent, d.absent_amount,
                          (v) => setField(r.emp_code, { deduct_absent: v }), (v) => setField(r.emp_code, { absent_amount: v }))}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-right">{r.late_minutes || '-'}</td>
                        <td className="px-3 py-2">{cell(r.allowed.late, d.deduct_late, d.late_amount,
                          (v) => setField(r.emp_code, { deduct_late: v }), (v) => setField(r.emp_code, { late_amount: v }))}</td>
                        <td className="px-3 py-2">
                          <input
                            type="text" value={d.note} disabled={readOnly} maxLength={200}
                            onChange={(e) => setField(r.emp_code, { note: e.target.value })}
                            placeholder="เช่น ลืมสแกน"
                            className="input py-1 px-2 w-36"
                          />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right font-medium">{total > 0 ? baht(total) : '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-gray-600">
                          {baht(Math.max(0, r.pay_base - total))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-gray-400 mt-2">
            &ldquo;สุทธิโดยประมาณ&rdquo; = ฐานเงินเดือน − ยอดหักในตารางนี้เท่านั้น ยังไม่รวมเงินเพิ่ม/เงินหักอื่นและภาษี/ประกันสังคม ·
            ยอดจริงดูได้ที่หน้าทำเงินเดือนหลังกดรันร่าง
          </p>
        </>
      )}
    </AdminLayout>
  );
}

export default OwnerDockingReview;
